/**
 * @file index.tsx
 * @description MultipleCascader 级联面板主入口
 *
 * 模块职责：
 * - 级联面板的“列渲染 + 交互”实现
 * - 管理展开链路（expandedPath）、搜索过滤（filteredData）、列构建（columns）
 * - 协调 SearchHeader、OptionRow 等子组件的渲染与事件
 *
 * 业务约束（来源：doc/requirements.md）：
 * - 点击“菜单行/箭头”只影响展开链路，不影响已选 value
 * - 点击“复选框”只影响已选 value，不改变展开链路
 * - 禁用项不可勾选，但若有子级仍允许展开
 * - 搜索仅影响候选展示与展开链路，不应清空已选 value
 *
 * 依赖子模块：
 * - panelUtils：纯工具函数
 * - SearchHeader：搜索框 UI
 * - OptionRow：单行选项渲染与交互
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'umi';

import type {
  ICascaderChangeContextCheck,
  ICascaderMenuOption,
  IMultipleCascaderProps,
  TStringPath,
} from '../types';
import { findPathOptions, getSelectedOptions } from '../utils';
import {
  buildColumns,
  collectLeafPaths,
  filterTreeByKeyword,
  fixExpandedPathByData,
  getDefaultExpandedPath,
  isPrefixPath,
} from './panelUtils';
import OptionRow from './OptionRow';
import SearchHeader, { type ISearchTagItem } from './SearchHeader';

/** 级联面板 Props */
interface IMultipleCascaderPanelProps {
  data: ICascaderMenuOption[];
  value: TStringPath[];
  columnTitles?: React.ReactNode[];
  onChange: IMultipleCascaderProps['onChange'];
  disabled?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  onSearch?: IMultipleCascaderProps['onSearch'];
  searchValue?: string;
  onSearchValueChange?: (nextValue: string) => void;
}

/**
 * 级联面板内部实现。
 *
 * 错误处理策略：
 * - 路径查找失败时回退为空链路，让 UI 保持可用
 * - 选中值 value 一律视作外部真实来源，搜索/展开不会篡改它
 */
const MultipleCascaderPanelInner: React.FC<IMultipleCascaderPanelProps> = (
  props,
) => {
  const {
    data,
    value,
    columnTitles,
    onChange,
    disabled,
    searchable,
    searchPlaceholder,
    onSearch,
    searchValue,
    onSearchValueChange,
  } = props;

  const { formatMessage } = useIntl();
  const [expandedPath, setExpandedPath] = useState<string[]>([]);

  const effectiveSearchable = searchable !== false;
  const keyword = effectiveSearchable ? searchValue || '' : '';

  const filteredData = useMemo(
    () =>
      effectiveSearchable ? filterTreeByKeyword(data, keyword) : data,
    [data, effectiveSearchable, keyword],
  );

  useEffect(() => {
    if (!Array.isArray(filteredData) || filteredData.length === 0) {
      setExpandedPath([]);
      return;
    }
    setExpandedPath((prev) => {
      if (prev.length > 0) {
        return fixExpandedPathByData(filteredData, prev);
      }
      return getDefaultExpandedPath(filteredData);
    });
  }, [filteredData]);

  const columns = useMemo(
    () => buildColumns(filteredData, expandedPath),
    [expandedPath, filteredData],
  );

  const isSearchNoResult =
    effectiveSearchable &&
    keyword.trim() !== '' &&
    (!Array.isArray(filteredData) || filteredData.length === 0);

  const searchTags: ISearchTagItem[] = useMemo(() => {
    const getNodeText = (node?: ICascaderMenuOption): string => {
      if (!node) return '';
      if (typeof node.label === 'string' || typeof node.label === 'number') {
        return String(node.label);
      }
      return node.value || '';
    };

    const chains = getSelectedOptions(data, value);
    return chains.map((chain) => {
      const path = chain.map((node) => node.value);
      const level1Text = getNodeText(chain[0]);
      const level2Text = getNodeText(chain[1]);
      const last = chain[chain.length - 1];
      const currentText = getNodeText(last);
      const tagTextParts = [level1Text, level2Text, currentText].filter(Boolean);
      return {
        pathKey: path.join('/'),
        path,
        label: tagTextParts.join(':'),
        disabled: last?.disabled,
      };
    });
  }, [data, value]);

  const handleRemoveSearchTag = useCallback(
    (path: TStringPath): void => {
      const nextValue = value.filter((item) => !isPrefixPath(path, item));
      const optionChain = findPathOptions(data, path) || [];
      const option =
        optionChain.length > 0
          ? optionChain[optionChain.length - 1]
          : { value: path[path.length - 1] ?? '', label: '' };

      const context: ICascaderChangeContextCheck = {
        type: 'uncheck',
        path,
        option,
        optionChain,
        value: nextValue,
        originContext: undefined,
      };
      onChange?.(nextValue, context);
    },
    [data, onChange, value],
  );

  const handleClearSearchBar = useCallback((): void => {
    const safeValue = '';
    onSearchValueChange?.(safeValue);
    onSearch?.(safeValue);
    if (value.length > 0) {
      onChange?.([], { type: 'clear', value: [] });
    }
  }, [onChange, onSearch, onSearchValueChange, value.length]);

  const handleClickOption = useCallback(
    (option: ICascaderMenuOption, level: number): void => {
      if (!option.children || option.children.length === 0) {
        setExpandedPath((prev) => {
          const next = prev.slice(0, level);
          next[level] = option.value;
          return next;
        });
        return;
      }

      setExpandedPath((prev) => {
        const prefix = prev.slice(0, level);
        const basePath = [...prefix, option.value];
        let deepPath = [...basePath];
        let currentLevel = option.children || [];

        while (Array.isArray(currentLevel) && currentLevel.length > 0) {
          const first = currentLevel[0];
          deepPath = [...deepPath, first.value];
          currentLevel = first.children || [];
        }
        return deepPath;
      });
    },
    [],
  );

  const handleCheckboxChange = useCallback(
    (
      checked: boolean,
      option: ICascaderMenuOption,
      level: number,
      originContext: unknown,
    ): void => {
      const prefix = expandedPath.slice(0, level);
      const path: TStringPath = [...prefix, option.value];

      const optionChain = findPathOptions(filteredData, path) || [];
      const targetOption =
        optionChain.length > 0 ? optionChain[optionChain.length - 1] : option;
      const leafPaths =
        targetOption && optionChain.length > 0
          ? collectLeafPaths(targetOption, path)
          : [];

      let nextValue: TStringPath[] = [];

      if (checked) {
        const existingKeys = new Set<string>();
        value.forEach((item) => {
          existingKeys.add(item.join('/'));
          nextValue.push(item);
        });

        const pathKey = path.join('/');
        if (!existingKeys.has(pathKey)) {
          nextValue.push(path);
          existingKeys.add(pathKey);
        }

        leafPaths.forEach((leafPath) => {
          const leafKey = leafPath.join('/');
          if (!existingKeys.has(leafKey)) {
            nextValue.push(leafPath);
            existingKeys.add(leafKey);
          }
        });
      } else {
        nextValue = value.filter((item) => !isPrefixPath(path, item));
      }

      const context: ICascaderChangeContextCheck = {
        type: checked ? 'check' : 'uncheck',
        path,
        option,
        optionChain,
        value: nextValue,
        originContext,
      };
      onChange?.(nextValue, context);
    },
    [expandedPath, filteredData, onChange, value],
  );

  const handleSearchChange = useCallback(
    (nextValue: string): void => {
      const safeValue = String(nextValue || '');
      onSearchValueChange?.(safeValue);
      onSearch?.(safeValue);
    },
    [onSearch, onSearchValueChange],
  );

  return (
    <div className="dbl-multiple-cascader-panel">
      {effectiveSearchable ? (
        <SearchHeader
          placeholder={searchPlaceholder}
          searchKeyword={keyword}
          disabled={disabled}
          onSearchKeywordChange={handleSearchChange}
          tags={searchTags}
          onRemoveTag={handleRemoveSearchTag}
          onClearBar={handleClearSearchBar}
        />
      ) : null}
      {isSearchNoResult ? (
        <div className="dbl-multiple-cascader-status">
          {formatMessage({ id: 'dbl_common_search_nothing' })}
        </div>
      ) : (
        <div className="dbl-multiple-cascader-columns">
          {columns.map((options, level) => {
            const titleNode = Array.isArray(columnTitles)
              ? columnTitles[level]
              : null;

            return (
              <div key={level} className="dbl-multiple-cascader-column">
                {titleNode ? (
                  <div className="dbl-multiple-cascader-menu-title dbl-multiple-cascader-column-title">
                    {titleNode}
                  </div>
                ) : null}
                <div className="dbl-multiple-cascader-column-list">
                  {options.map((option) => (
                    <OptionRow
                      key={option.value}
                      option={option}
                      level={level}
                      expandedPath={expandedPath}
                      value={value}
                      filteredData={filteredData}
                      onExpand={handleClickOption}
                      onCheckboxChange={handleCheckboxChange}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const MultipleCascaderPanel = React.memo(MultipleCascaderPanelInner);
MultipleCascaderPanel.displayName = 'MultipleCascaderPanel';

export default MultipleCascaderPanel;
